import * as React from 'react'
import useRouter from 'use-react-router'
import * as O from 'fp-ts/lib/Option'

import {
  closeWithSideEffect,
  UserConfig
} from '@espoon-voltti/react-voltti/components/modal'

import { GlobalModal } from '@espoon-voltti/nuora-app-common/globals'
import { Auth } from '@espoon-voltti/nuora-app-common/services/use-auth'

import { t } from '../../i18n'
import {
  ProjectGrantDraftApp,
  GrantApp,
  ValidDraftApp,
  SigningApp,
  ValidGeneralGrantDraftApp,
  GeneralGrantDraftApp
} from '../../domain'
import { Store } from '../../services/use-store'
import { handleError } from '../../error-handling'
import { localizedPathnames } from '../../utils/localized-pathname'

import { maybeValidApp, maybeValidGeneralGrantApp } from './validation'
import { AppForm } from './form'
import { AppPreview } from './preview'
import { AppSigning } from './signing'
import { setModalFocus } from '../../utils/modal-focus'

export const AppView: React.FC<{ appId: string }> = ({ appId }) => {
  const router = useRouter()
  const store = Store.use()
  const auth = Auth.use()
  const maybeApp = store.appById(appId)
  const maybeSignable = maybeValidForSigning(maybeApp)
  const maybeGrantPeriod = store.grantPeriods.find(
    (x) => x.id === maybeApp?.grantPeriodId
  )

  const isSigningRoute = router.match.path.endsWith('/signing')

  const handleSave = (x: ProjectGrantDraftApp) =>
    store.saveDraft(x).then(store.setApp).catch(handleError)

  const handleClose = () => router.history.push(localizedPathnames.home(org.id))

  const handleDelete = () => {
    GlobalModal.get().showCancellable(
      {
        dataQa: 'confirmDeleteDialog',
        title: t(['common', 'modal', 'deleteApp', 'title']),
        text: t(['common', 'modal', 'deleteApp', 'text']),
        confirmButtonLabel: t([
          'common',
          'modal',
          'deleteApp',
          'confirmButtonLabel'
        ])
      },
      closeWithSideEffect(() => {
        const orgId = org.id
        store
          .deleteApp(app.id)
          .then(() => router.history.push(localizedPathnames.home(orgId)))
          .catch(handleError)
      })
    )
    setModalFocus()
  }

  const handleSubmit = (x: ProjectGrantDraftApp) => {
    store
      .saveDraft(x)
      .then((saved) => {
        store.setApp(x)
        router.history.push(localizedPathnames.signing(x.id))
      })
      .catch(handleError)
  }
  const handleSign = (
    x: ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp
  ) => {
    store
      .signApp(org, x)
      .then((signed) => {
        GlobalModal.get().show(
          signed.state === 'SIGNING'
            ? awaitSuccessConfig()
            : signedSuccessConfig(),
          closeWithSideEffect(() => store.setApp(signed))
        )
        setModalFocus()
      })
      .catch(handleError)
  }

  React.useEffect(() => {
    if (maybeApp != null) {
      if (!isSigningRoute && maybeApp.state === 'SIGNING') {
        router.history.replace(localizedPathnames.signing(maybeApp.id))
      } else if (isSigningRoute && !O.isSome(maybeSignable)) {
        router.history.replace(localizedPathnames.application(maybeApp.id))
      }
    }
  }, [router.location, maybeApp])

  if (maybeApp == null) {
    return <div />
  }
  const app: GrantApp = maybeApp
  const org = store.orgById(app.applicant.org.id)

  if (!isSigningRoute && app.state === 'DRAFT' && maybeGrantPeriod) {
    return (
      <AppForm
        grantPeriod={maybeGrantPeriod}
        app={app}
        org={org}
        onChange={handleSave}
        onClose={handleClose}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
      />
    )
  }

  if (
    !isSigningRoute &&
    app.state !== 'DRAFT' &&
    app.state !== 'SIGNING' &&
    maybeGrantPeriod
  ) {
    return <AppPreview app={app} grantPeriod={maybeGrantPeriod} />
  }
  if (isSigningRoute && O.isSome(maybeSignable) && maybeGrantPeriod) {
    return (
      <AppSigning
        grantPeriod={maybeGrantPeriod}
        app={maybeSignable.value}
        org={org}
        user={auth}
        onClose={handleClose}
        onDelete={handleDelete}
        onSign={handleSign}
      />
    )
  }

  return <div />
}

function isProjectGrantForm(
  object?: GrantApp | ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp
): object is ProjectGrantDraftApp {
  return object?.form.type === 'PROJECT_GRANT'
}
function isGeneralGrantForm(object?: GrantApp): object is GeneralGrantDraftApp {
  return object?.form.type === 'GENERAL_GRANT'
}

const maybeValidForSigning = (
  app?: GrantApp
): O.Option<ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp> => {
  if (app?.state === 'DRAFT' && isProjectGrantForm(app)) {
    return maybeValidApp(app)
  } else if (app?.state === 'DRAFT' && isGeneralGrantForm(app)) {
    return maybeValidGeneralGrantApp(app)
  }

  if (app?.state === 'SIGNING') {
    return O.some(app)
  }

  return O.none
}

const awaitSuccessConfig = (): UserConfig => ({
  dataQa: 'awaitSuccess',
  type: 'success',
  title: t(['views', 'signing', 'await', 'title']),
  text: t(['views', 'signing', 'await', 'text']),
  confirmButtonLabel: t(['views', 'signing', 'await', 'controls', 'confirm'])
})

const signedSuccessConfig = (): UserConfig => ({
  dataQa: 'sendSuccessDialog',
  type: 'success',
  title: t(['views', 'signing', 'success', 'title']),
  text: t(['views', 'signing', 'success', 'text']),
  confirmButtonLabel: t(['views', 'signing', 'success', 'controls', 'confirm'])
})
